import React from "react";

export const HomeSkeleton = () => (
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 animate-pulse space-y-8">
    {/* Top bar skeleton */}
    <div className="flex items-center justify-between py-2 border-b border-gray-100">
      <div className="h-10 w-36 bg-gray-200 rounded-lg"></div>
      <div className="h-10 w-64 bg-gray-200 rounded-full hidden sm:block"></div>
      <div className="flex gap-3">
        <div className="h-10 w-10 bg-gray-200 rounded-full"></div>
        <div className="h-10 w-24 bg-gray-200 rounded-lg"></div>
      </div>
    </div>

    {/* Hero banner skeleton */}
    <div className="w-full h-48 sm:h-64 md:h-80 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 rounded-2xl"></div>

    {/* Categories skeleton */}
    <div>
      <div className="h-6 w-48 bg-gray-200 rounded mb-4"></div>
      <div className="flex gap-4 overflow-hidden">
        {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
          <div key={i} className="flex flex-col items-center flex-shrink-0 space-y-2">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gray-200 rounded-full"></div>
            <div className="h-3 w-12 bg-gray-200 rounded"></div>
          </div>
        ))}
      </div>
    </div>

    {/* Filter chips skeleton */}
    <div className="flex gap-3 overflow-hidden pt-2">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <div key={i} className="h-9 w-28 bg-gray-200 rounded-full flex-shrink-0"></div>
      ))}
    </div>

    {/* Restaurants Grid skeleton */}
    <div>
      <div className="h-7 w-56 bg-gray-200 rounded mb-6"></div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
          <div key={i} className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm space-y-3 p-3">
            <div className="w-full h-44 bg-gray-200 rounded-xl"></div>
            <div className="flex justify-between items-center">
              <div className="h-5 w-3/5 bg-gray-200 rounded"></div>
              <div className="h-5 w-12 bg-gray-200 rounded"></div>
            </div>
            <div className="h-4 w-4/5 bg-gray-200 rounded"></div>
            <div className="flex justify-between items-center pt-2 border-t border-gray-50">
              <div className="h-4 w-20 bg-gray-200 rounded"></div>
              <div className="h-4 w-16 bg-gray-200 rounded"></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

export const RestaurantDetailSkeleton = () => (
  <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 animate-pulse space-y-8">
    {/* Nav skeleton */}
    <div className="flex items-center justify-between py-2 border-b border-gray-100">
      <div className="flex items-center gap-4">
        <div className="h-9 w-9 bg-gray-200 rounded-full"></div>
        <div className="h-9 w-32 bg-gray-200 rounded-lg"></div>
      </div>
      <div className="h-9 w-48 bg-gray-200 rounded-full"></div>
    </div>

    {/* Restaurant Hero banner */}
    <div className="w-full h-56 sm:h-72 md:h-88 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 rounded-3xl relative overflow-hidden">
      <div className="absolute bottom-6 left-6 right-6 flex justify-between items-end">
        <div className="space-y-2">
          <div className="h-8 w-64 bg-gray-300 rounded-lg"></div>
          <div className="h-4 w-44 bg-gray-300 rounded"></div>
          <div className="h-4 w-32 bg-gray-300 rounded"></div>
        </div>
        <div className="h-12 w-16 bg-gray-300 rounded-xl hidden sm:block"></div>
      </div>
    </div>

    {/* Menu Category Pills */}
    <div className="flex gap-3 overflow-hidden border-b border-gray-100 pb-3">
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="h-9 w-24 bg-gray-200 rounded-full flex-shrink-0"></div>
      ))}
    </div>

    {/* Dishes grid skeleton */}
    <div className="space-y-4">
      <div className="h-7 w-40 bg-gray-200 rounded"></div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="flex justify-between p-4 rounded-2xl border border-gray-100 shadow-sm bg-white gap-4">
            <div className="space-y-2 flex-1">
              <div className="h-5 w-3/4 bg-gray-200 rounded"></div>
              <div className="h-4 w-16 bg-gray-200 rounded"></div>
              <div className="h-3 w-full bg-gray-200 rounded"></div>
              <div className="h-3 w-2/3 bg-gray-200 rounded"></div>
            </div>
            <div className="w-28 h-28 bg-gray-200 rounded-xl flex-shrink-0"></div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

export const SearchSkeleton = () => (
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 animate-pulse space-y-8">
    {/* Filter chips skeleton */}
    <div className="flex gap-3 overflow-hidden">
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="h-9 w-28 bg-gray-200 rounded-full flex-shrink-0"></div>
      ))}
    </div>

    {/* Section 1: Restaurants grid skeleton */}
    <div className="space-y-4">
      <div className="h-6 w-44 bg-gray-200 rounded"></div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm space-y-3 p-3">
            <div className="w-full h-36 bg-gray-200 rounded-xl"></div>
            <div className="h-5 w-3/4 bg-gray-200 rounded"></div>
            <div className="flex justify-between items-center">
              <div className="h-4 w-20 bg-gray-200 rounded"></div>
              <div className="h-4 w-16 bg-gray-200 rounded"></div>
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* Section 2: Dishes grid skeleton */}
    <div className="space-y-4 pt-4">
      <div className="h-6 w-36 bg-gray-200 rounded"></div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="flex justify-between p-4 rounded-2xl border border-gray-100 shadow-sm bg-white gap-4">
            <div className="space-y-2 flex-1">
              <div className="h-5 w-3/4 bg-gray-200 rounded"></div>
              <div className="h-4 w-16 bg-gray-200 rounded"></div>
              <div className="h-3 w-full bg-gray-200 rounded"></div>
            </div>
            <div className="w-20 h-20 bg-gray-200 rounded-xl flex-shrink-0"></div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

export const ProfileSkeleton = () => (
  <div className="max-w-4xl mx-auto px-4 py-8 animate-pulse space-y-6">
    <div className="flex items-center gap-6 p-6 bg-white rounded-2xl border border-gray-100 shadow-sm">
      <div className="w-20 h-20 rounded-full bg-gray-200"></div>
      <div className="space-y-3 flex-1">
        <div className="h-6 w-48 bg-gray-200 rounded"></div>
        <div className="h-4 w-32 bg-gray-200 rounded"></div>
      </div>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="p-5 bg-white rounded-2xl border border-gray-100 shadow-sm space-y-3">
          <div className="h-4 w-28 bg-gray-200 rounded"></div>
          <div className="h-10 w-full bg-gray-200 rounded-xl"></div>
        </div>
      ))}
    </div>
  </div>
);

export const OrderTrackingSkeleton = () => (
  <div className="max-w-3xl mx-auto px-4 py-8 animate-pulse space-y-6">
    <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm space-y-4">
      <div className="flex justify-between items-center">
        <div className="h-6 w-40 bg-gray-200 rounded"></div>
        <div className="h-6 w-24 bg-gray-200 rounded-full"></div>
      </div>
      <div className="h-4 w-48 bg-gray-200 rounded"></div>
      <div className="grid grid-cols-4 gap-2 pt-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="flex flex-col items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gray-200"></div>
            <div className="h-3 w-14 bg-gray-200 rounded"></div>
          </div>
        ))}
      </div>
    </div>
    <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm space-y-3">
      <div className="h-5 w-32 bg-gray-200 rounded"></div>
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex justify-between items-center py-2 border-b border-gray-50">
          <div className="h-4 w-36 bg-gray-200 rounded"></div>
          <div className="h-4 w-16 bg-gray-200 rounded"></div>
        </div>
      ))}
    </div>
  </div>
);

export const ListCardSkeleton = ({ count = 3 }) => (
  <div className="max-w-4xl mx-auto px-4 py-6 animate-pulse space-y-4">
    <div className="h-7 w-48 bg-gray-200 rounded mb-4"></div>
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="p-5 bg-white rounded-2xl border border-gray-100 shadow-sm flex justify-between items-center gap-4">
        <div className="space-y-2 flex-1">
          <div className="h-5 w-44 bg-gray-200 rounded"></div>
          <div className="h-4 w-3/4 bg-gray-200 rounded"></div>
        </div>
        <div className="w-10 h-10 rounded-full bg-gray-200 flex-shrink-0"></div>
      </div>
    ))}
  </div>
);

export const TextContentSkeleton = () => (
  <div className="max-w-4xl mx-auto px-4 py-8 animate-pulse space-y-4">
    <div className="h-8 w-64 bg-gray-200 rounded mb-6"></div>
    <div className="space-y-3 bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
      {[1, 2, 3, 4, 5, 6, 7].map((i) => (
        <div key={i} className="h-4 bg-gray-200 rounded" style={{ width: `${85 + (i % 3) * 5}%` }}></div>
      ))}
    </div>
  </div>
);

export const MapSkeleton = () => (
  <div className="w-full h-96 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 rounded-3xl animate-pulse flex flex-col items-center justify-center space-y-3 border border-gray-100">
    <div className="w-12 h-12 rounded-full bg-gray-300 animate-ping"></div>
    <div className="h-4 w-36 bg-gray-300 rounded"></div>
  </div>
);

export const CheckoutSkeleton = () => (
  <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-pulse">
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <div className="lg:col-span-2 space-y-6">
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm space-y-4">
          <div className="h-6 w-48 bg-gray-200 rounded"></div>
          <div className="h-4 w-3/4 bg-gray-200 rounded"></div>
          <div className="h-12 w-full bg-gray-200 rounded-xl"></div>
        </div>
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm space-y-4">
          <div className="h-6 w-40 bg-gray-200 rounded"></div>
          <div className="grid grid-cols-4 gap-3">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-10 bg-gray-200 rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
      <div className="space-y-6">
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm space-y-4">
          <div className="h-6 w-36 bg-gray-200 rounded"></div>
          {[1, 2, 3].map(i => (
            <div key={i} className="flex justify-between items-center py-2 border-b border-gray-50">
              <div className="h-4 w-32 bg-gray-200 rounded"></div>
              <div className="h-4 w-12 bg-gray-200 rounded"></div>
            </div>
          ))}
          <div className="h-12 w-full bg-gray-200 rounded-xl pt-2"></div>
        </div>
      </div>
    </div>
  </div>
);


